<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import PerformanceLevelNotSetBadge from '@/core/badges/PerformanceLevelNotSetBadge.vue';
import plusMinus from '@/assets/school-grades/plus-minus.svg';
import SchoolGradeModifierSymbol from './SchoolGradeModifierSymbol.vue';
import type { ISchoolGradePresentation } from 'common/utils/school.grade.utils';

defineOptions({ inheritAttrs: false });
const props = defineProps<{
  presentation: ISchoolGradePresentation;
  selected?: boolean;
  disabled?: boolean;
  dimmed?: boolean;
  modifierAvailable?: boolean;
  modifierOpen?: boolean;
  inputLength?: number;
  fluid?: boolean;
  disableTransitions?: boolean;
}>();
const emit = defineEmits<{ select: [event: MouseEvent]; modifier: [] }>();
const modifierButton = useTemplateRef<HTMLButtonElement>('modifierButton');
defineExpose({ modifierButton });
const { t } = useI18n();
const chipClasses = computed(() => {
  const length = props.inputLength ?? props.presentation.text.length;
  let textClass = '!text-heading';
  if (length >= 4) textClass = '!text-footer';
  else if (length === 3) textClass = '!text-body';
  return [
    '!p-0 !leading-body tracking-label motion-safe:transition-[color,border-color] motion-safe:duration-160 motion-safe:ease-[ease]',
    props.fluid ? '!w-full !h-auto !min-h-0 aspect-square' : '!size-icon-xl !min-h-icon-xl',
    textClass,
    // Center the 3px stroke on the fixed 40px chip.
    props.selected && '!border-transparent !outline !outline-[3px] !outline-current !outline-offset-[-1.5px]',
  ];
});
const modifierClasses = computed(() => {
  if (props.presentation.modifier === 1) return 'bg-success text-white';
  if (props.presentation.modifier === -1) return 'bg-error text-white';
  return 'bg-surface-primary text-secondary shadow-elevated after:content-empty after:pointer-events-none after:absolute after:-inset-px after:rounded-full after:border after:border-quaternary';
});
</script>

<template>
  <div class="group/grade-option relative shrink-0">
    <slot :chip-classes="chipClasses">
      <button
        v-bind="$attrs"
        type="button"
        class="rounded-sm border bg-surface-primary font-semibold text-tint"
        :class="[chipClasses, presentation.label ? 'border-quaternary' : 'border-divider']"
        :disabled="disabled"
        :aria-pressed="selected"
        :aria-label="($attrs['aria-label'] as string | undefined) ?? (presentation.label || undefined)"
        @click.stop="emit('select', $event)"
      >
        <span
          v-if="presentation.label"
          data-grade-digit
          class="motion-safe:transition-opacity motion-safe:duration-160 motion-safe:ease-linear"
          :class="{ 'opacity-20 grayscale': dimmed, '!transition-none': disableTransitions }"
          >{{ presentation.text }}</span
        >
        <PerformanceLevelNotSetBadge v-else class="size-full !rounded-sm p-xs !border-0" />
      </button>
    </slot>
    <button
      v-if="modifierAvailable"
      ref="modifierButton"
      type="button"
      data-grade-modifier
      class="absolute -right-[6px] -top-[6px] size-icon-xs items-center justify-center rounded-full text-footer font-semibold leading-none before:absolute before:-inset-sm focus-visible:outline-2 focus-visible:outline-tint focus-visible:outline-offset-2"
      :class="[
        modifierClasses,
        presentation.modifier || modifierOpen
          ? 'flex'
          : 'hidden group-hover/grade-option:flex group-focus-within/grade-option:flex focus-visible:flex max-md:flex [@media(hover:none)]:flex',
      ]"
      :disabled="disabled"
      :aria-label="t('schoolGrades.modifier')"
      aria-haspopup="dialog"
      :aria-expanded="modifierOpen"
      @click.stop="emit('modifier')"
    >
      <SchoolGradeModifierSymbol v-if="presentation.modifier" :modifier="presentation.modifier" class="size-icon-xxs" />
      <img v-else :src="plusMinus" class="h-icon-xxs w-[5px]" alt="" aria-hidden="true" draggable="false" />
    </button>
    <span
      v-else-if="presentation.modifier"
      class="pointer-events-none absolute -right-[6px] -top-[6px] flex size-icon-xs items-center justify-center rounded-full"
      :class="modifierClasses"
      aria-hidden="true"
    >
      <SchoolGradeModifierSymbol :modifier="presentation.modifier" class="size-icon-xxs" />
    </span>
  </div>
</template>
